import { useCallback, useEffect, useRef, useState } from 'react';
import uuid from 'uuid/v4';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import { useConversationById } from '@atlassian/conversation-assistant/controllers/chat-store';
import { findLastAssistantAction } from '@atlassian/conversation-assistant/controllers/chat-store/utils';
import { useSmartCreationChatStoreApi } from '@atlassian/conversation-assistant/controllers/smart-creation-api';
import { isAIFCAction } from '@atlassian/conversation-assistant-action-helpers/utils';
import {
	CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY,
	CONFLUENCE_AIFC_WHITEBOARD_UPDATE_KEY,
	getDynamicUiSubtype,
	isRemixGeneratedFlow,
} from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions';
import type { InteractionSource } from '@atlassian/conversation-assistant-instrumentation/constants/analytics';
import type { StoreChatItem } from '@atlassian/conversation-assistant-store/controllers/chat/types';
import {
	CHAT_STORE_STATUS,
	isStoreAssistant,
	isStoreError,
	isStoreHuman,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { useChatMessages } from '@atlassian/conversation-assistant-store/controllers/chat/use-chat-messages';
import type { StoreError } from '@atlassian/conversation-assistant-store/controllers/chat-signals';
import {
	activateRemixCancellationObservation,
	recordRemixCancellation,
	type RemixCancellationIntent,
} from '@atlassian/conversation-assistant-store/controllers/remix-cancellation-signals';
import { useStagingAreaActions } from '@atlassian/conversation-assistant-store/controllers/staging-area';
import { useUserPreferences } from '@atlassian/conversation-assistant-store/controllers/user-preferences';
import {
	getSubTypeDisplayName,
	getTypeDisplayName,
} from '@atlassian/malleable-ui-components/remix/remix-overlay/utils';
import type { RemixOptionSelectionParameters } from '@atlassian/malleable-ui-components/remix/remix-overlay/types';

import { usePageTitle } from '@confluence/content-title/entry-points/usePageTitle.ts';
import { expVal, UNSAFE_expValEqualsNoExposure } from '@confluence/feature-experiments';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { useContentType, usePageContentId, usePageSpaceKey } from '@confluence/page-context';
import { useContentStatus } from '@confluence/page-utils/entry-points/useContentStatus.ts';
import {
	Message,
	publishMessage,
} from '@confluence/page-tree-refresh-state-container/entry-points/messageBus.ts';
import { useSmartCreationModalActions } from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore.tsx';

import type { DynamicUiSource } from './common-types.ts';

const isActionComplete = (chatMessages: StoreChatItem[]): boolean => {
	// Scan backwards to find ACTION_COMPLETE in the latest turn.
	// If we encounter a HUMAN message first, that marks a turn boundary —
	// any ACTION_COMPLETE before it belongs to a previous turn.
	let hasActionCompleteStep = false;
	for (let i = chatMessages.length - 1; i >= 0; i--) {
		const message = chatMessages[i];

		if (message.type === 'ACTION_COMPLETE') {
			hasActionCompleteStep = true;
			break;
		}

		if (isStoreHuman(message)) {
			// Stop searching when we reach the end of the most recent turn.
			break;
		}
	}

	const lastPlannedAIFCAction = findLastAssistantAction(
		chatMessages,
		(action) => isAIFCAction(action) && action.status === 'planned',
	);
	const hasCompletedAction = lastPlannedAIFCAction !== undefined;

	if (fg('remix_product_modal_rovo_chat')) {
		return hasActionCompleteStep || hasCompletedAction;
	} else {
		return hasActionCompleteStep;
	}
};

/**
 * True when the latest turn contains ACTION_PREPARE but no ACTION_COMPLETE.
 * This happens when AdfEditorMinion early-exits with "no operations returned
 * by LLM" — the stream completes but the action is never sealed.
 */
const hasActionPrepareWithoutComplete = (chatMessages: StoreChatItem[]): boolean => {
	let hasActionPrepare = false;
	for (let i = chatMessages.length - 1; i >= 0; i--) {
		const message = chatMessages[i];

		if (message.type === 'ACTION_COMPLETE') {
			return false;
		}

		if (message.type === 'ACTION_PREPARE') {
			hasActionPrepare = !!message.action && isRemixGeneratedFlow(message.action);
		}

		if (isStoreHuman(message)) {
			break;
		}
	}

	return hasActionPrepare;
};

function shouldOpenEphemeralPreview(chatMessages: StoreChatItem[]): boolean {
	for (let i = chatMessages.length - 1; i >= 0; i--) {
		const message = chatMessages[i];
		if (message.type === 'ACTION_PREPARE') {
			if (!message.action) {
				return false;
			}
			if (!isRemixGeneratedFlow(message.action)) {
				return false;
			}
			if (isWhiteboardRemixActionKey(message.action.key) && !getDynamicUiSubtype(message.action)) {
				return false;
			}
			return true;
		}
	}
	return false;
}

const isWhiteboardRemixActionKey = (key: string): boolean =>
	key === CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY || key === CONFLUENCE_AIFC_WHITEBOARD_UPDATE_KEY;

/**
 * True when the latest matching assistant action is a whiteboard create/update
 * and checks if the action is in the ACTION_PART streaming status.
 * (`MessageAction.status === 'streaming'`).
 */
const hasWhiteboardRemixActionStreaming = (chatMessages: StoreChatItem[]): boolean =>
	findLastAssistantAction(
		chatMessages,
		(action) =>
			isWhiteboardRemixActionKey(action.key) && action.status?.toLowerCase() === 'streaming',
	) !== undefined;

/**
 * Controls how the current page content is represented when opening the AIFC
 * staging modal from a remix flow.
 *
 * - `current-content`: include the current content as editable staging-area
 *   content via `context.confluence.content`.
 * - `generating-from-content`: keep the current content only as parent/source
 *   context via `parentContent`, leaving the generated output to become the
 *   staging-area content target.
 */
type StagingAreaTargetMode = 'current-content' | 'generating-from-content';
type RemixAdditionalCreationContextParams = { experience: 'remix_object' } & (
	| { contentTypes: string[] }
	| { blocks: string[] }
);

/**
 * Hands a remix request off to the AIFC / Create with Rovo flow.
 *
 * The hook creates and tracks a remix conversation, sends the initial prompt
 * with the appropriate creation context, and exposes helpers for opening the
 * staging modal once the generated action is ready.
 *
 * @param onActionReady - Optional callback invoked when the action is ready for
 *   the consumer to react (e.g. open the staging modal). For most actions this
 *   fires on ACTION_COMPLETE; for whiteboard create/update actions it fires
 *   earlier once the assistant action reaches `streaming` status (after
 *   ACTION_PART chunks merge into the assistant message).
 * @param stagingAreaTargetMode - Controls whether the current content should be
 *   registered as staging-area content when opening the modal, or kept only as
 *   parent/source context for generated-content flows.
 * @param trackRemixConversationById - Keeps view-mode Remix state attached to
 *   the conversation created by this hook after the side chat deactivates it.
 */
export const useRemixToAIFC = ({
	onActionReady,
	stagingAreaTargetMode = 'current-content',
	onPreviewOpened,
	onSubmitted,
	onPreviewDismissed,
	trackRemixConversationById = false,
}: {
	onActionReady?: () => void;
	stagingAreaTargetMode?: StagingAreaTargetMode;
	onPreviewOpened?: () => void;
	onSubmitted?: () => void;
	onPreviewDismissed?: () => void;
	trackRemixConversationById?: boolean;
} = {}): {
	sendAIFCPrompt: ({
		userPrompt,
		promptText,
		dynamicUiSubtype,
		dynamicUiType,
		dynamicUiSource,
		interactionSource,
		forcedContentType,
		additionalCreationContextParams,
		conversationId,
		remixCancellationSessionId,
	}: {
		userPrompt?: string;
		/** When provided, overrides the auto-generated prompt text sent to Rovo. */
		promptText?: string;
		dynamicUiType: RemixOptionSelectionParameters['type'];
		dynamicUiSubtype?: string;
		dynamicUiSource: DynamicUiSource | undefined;
		interactionSource?: InteractionSource;
		forcedContentType?: string;
		additionalCreationContextParams?: RemixAdditionalCreationContextParams;
		/** Reuse a Rovo-created conversation instead of minting a new one. */
		conversationId?: string;
		/** Exact renderer shadow generation to activate after conversation reset. */
		remixCancellationSessionId?: string;
	}) => Promise<string>;
	stopRemixing: () => void;
	isRemixError: boolean;
	isRemixAborted: boolean;
	isRemixCancelled: boolean;
	remixStoreError: StoreError | null;
	openAIFCStagingModal: (options?: { forceFullModal?: boolean }) => void;
	closeAIFCStagingModal: () => void;
	remixConversationId: string | undefined;
	resetRemixConversationId: () => void;
	setSelectedConversation: (params: { conversationId: string }) => void;
} => {
	const {
		sendMessage,
		createConversationOptimistically,
		abortRequests,
		activeConversation,
		setSelectedConversation,
	} = useSmartCreationChatStoreApi();
	const {
		setPrompt,
		setDynamicUiContext,
		setUserSelectedDynamicUiSubtype,
		setInteractionSource,
		setForceSkipAutoConversationSelection,
	} = useStagingAreaActions();
	const { preferences } = useUserPreferences();
	const { openModal, closeModal } = useSmartCreationModalActions();
	const [spaceKey] = usePageSpaceKey();
	const [contentId] = usePageContentId();
	const [contentType] = useContentType();
	const isLivePage = useIsCurrentPageLive();
	const { title: contentTitle } = usePageTitle({ contentId: contentId ?? '' });
	const { contentStatus } = useContentStatus();
	const isDraftPage = contentStatus === 'draft';

	// Track the conversation ID created by this remix flow. Only when the
	// store's activeConversation matches this ID do we consider it "ours".
	// Stored as state so that setting it triggers a rerender, ensuring
	// derived values (activeRemixConversation, chatMessages, etc.) update
	// immediately rather than waiting for an external rerender.
	const [remixConversationId, setRemixConversationId] = useState<string | undefined>(undefined);
	const outerCancellationIntentRef = useRef<RemixCancellationIntent>();

	const resetRemixConversationId = useCallback((): void => {
		outerCancellationIntentRef.current = undefined;
		setRemixConversationId(undefined);
	}, []);

	const remixConversation = useConversationById({
		conversationId: remixConversationId ?? '',
	});
	const shouldTrackRemixConversationById =
		trackRemixConversationById && expVal('cc_maui_polish_changes_batch_5', 'isEnabled', false);
	const activeRemixConversation = shouldTrackRemixConversationById
		? (remixConversation ?? null)
		: activeConversation?.id && activeConversation.id === remixConversationId
			? activeConversation
			: null;

	const [chatMessages] = useChatMessages({
		conversationId: shouldTrackRemixConversationById
			? (remixConversationId ?? '')
			: (activeRemixConversation?.id ?? ''),
	});
	const isRemixCancelled =
		shouldTrackRemixConversationById && remixConversation?.lastAgentMessageStatus === 'CANCELLED';

	// Extract the first StoreError from chatMessages so callers can surface
	// the specific Rovo error message as an experience attribute.
	const remixStoreError =
		chatMessages.find((message): message is StoreError => isStoreError(message)) ?? null;

	// Detect whether Rovo encountered an error while processing a remix request.
	// This covers ACTION_ERROR at any stage — both before the ephemeral preview
	// opens (during awaitingResponse) and while it is loading (during typingResponse).
	// Uses the same isStoreError check as the smart-creation staging area.
	const isRemixError =
		activeRemixConversation?.status === CHAT_STORE_STATUS.deactivatedAgentError ||
		remixStoreError !== null;

	const isRemixAborted =
		!isRemixError &&
		activeRemixConversation?.status === CHAT_STORE_STATUS.idle &&
		!isActionComplete(chatMessages) &&
		!hasActionPrepareWithoutComplete(chatMessages) &&
		chatMessages.some(isStoreAssistant);

	// Track whether the onActionReady callback has already been fired for the
	// current conversation to avoid duplicate invocations.
	const actionReadyFired = useRef(false);

	// Reset the fired flag when the active remix conversation changes (new remix flow).
	const prevConversationId = useRef(activeRemixConversation?.id);
	useEffect(() => {
		if (prevConversationId.current !== activeRemixConversation?.id) {
			actionReadyFired.current = false;
			prevConversationId.current = activeRemixConversation?.id;
		}
	}, [activeRemixConversation?.id]);

	// Fire onActionReady when the appropriate message arrives:
	// - For whiteboard create/update: when the action is `streaming` (early trigger so
	//   the whiteboard preview can start rendering while data is still arriving).
	// - For all other actions: when FINAL_RESPONSE is received from the stream,
	//   indicated by the conversation status transitioning to `idle`.
	// - Fallback: when the stream completed (idle) with ACTION_PREPARE but no
	//   ACTION_COMPLETE (AdfEditorMinion early-exit).
	useEffect(() => {
		if (actionReadyFired.current || !onActionReady || isRemixCancelled) {
			return;
		}

		const whiteboardStreamingDetected = hasWhiteboardRemixActionStreaming(chatMessages);
		const finalResponseReceived =
			activeRemixConversation?.status === CHAT_STORE_STATUS.idle && isActionComplete(chatMessages);
		const actionPreparedButNotCompleted =
			activeRemixConversation?.status === CHAT_STORE_STATUS.idle &&
			hasActionPrepareWithoutComplete(chatMessages);

		if (whiteboardStreamingDetected || finalResponseReceived || actionPreparedButNotCompleted) {
			actionReadyFired.current = true;
			onActionReady();
		}
	}, [chatMessages, onActionReady, activeRemixConversation?.status, isRemixCancelled]);

	const stopRemixing = useCallback((): void => {
		const remixCancellation = outerCancellationIntentRef.current;
		outerCancellationIntentRef.current = undefined;
		try {
			if (remixCancellation) {
				recordRemixCancellation(remixCancellation);
			}
		} finally {
			abortRequests?.({ conversationId: activeRemixConversation?.id });
		}
	}, [abortRequests, activeRemixConversation?.id]);

	const openAIFCStagingModal = useCallback(
		(options?: { forceFullModal?: boolean }): void => {
			// Chart/visualization previews can become ready after Rovo has already
			// deactivated the conversation. Do not let a stale completion callback
			// open an empty modal after the user stopped that exact Remix.
			if (isRemixCancelled) {
				return;
			}

			setForceSkipAutoConversationSelection(true);
			// Open the Confluence Smart Creation Modal with stagingAreaActive=true
			// to skip the prompt screen and go straight to the staging area preview.
			//
			// Only keep the ephemeral preview for remix-generated preview content.
			// The shared helper currently treats dynamic UI, image-based remix flows,
			// and remix-generated whiteboards/diagrams as ephemeral-preview candidates.
			// All other ACTION_PREPARE results should open the full AIFC modal.
			//
			// Pass forceFullModal: true to bypass this check
			const shouldUseEphemeralPreview = options?.forceFullModal
				? false
				: shouldOpenEphemeralPreview(chatMessages);

			openModal({
				entryPoint: 'dynamicUi',
				spaceKey: spaceKey ?? undefined,
				parentContent:
					contentId && !isDraftPage
						? {
								id: contentId,
								title: contentTitle ?? '',
								contentType: contentType ?? 'page',
							}
						: undefined,
				stagingAreaActive: true,
				isFloating: shouldUseEphemeralPreview,
				shouldShowBackButton: false,
				...(activeRemixConversation?.id &&
				UNSAFE_expValEqualsNoExposure('cc-maui-experiment', 'isEnabled', true)
					? { remixConversationId: activeRemixConversation.id }
					: {}),
				// `stagingAreaTargetMode` decides whether the current content is also
				// registered as staging-area content, or preserved only as parent/source
				// context for generated-content flows.
				...(stagingAreaTargetMode === 'current-content' && contentId
					? {
							content: {
								id: contentId,
								type: contentType ?? 'page',
								subType: isLivePage ? 'live' : null,
							},
						}
					: {}),
				// Pass a submitCallback only for ephemeral (floating) previews to
				// prevent navigating away from the current page. For the full modal flow
				// (e.g. "Add to Confluence"), omit the callback so the default
				// handleSubmitSmartCreate handler runs and redirects to the newly created content.
				...(shouldUseEphemeralPreview
					? {
							submitCallback: async () => {
								// Refresh the page tree for ephemeral previews only when the experiment is enabled.
								if (!expVal('cc-maui-refresh-page-tree-with-diagram', 'isEnabled', false)) {
									return;
								}
								if (contentId) {
									publishMessage(Message.PageTreeRefreshRequested, {
										parentId: contentId,
									});
								}
							},
						}
					: {}),
				onOpenCallback: onPreviewOpened,
				onSubmitCallback: onSubmitted,
				onCloseCallback: onPreviewDismissed,
			});
		},
		[
			openModal,
			spaceKey,
			contentId,
			contentType,
			contentTitle,
			isLivePage,
			isDraftPage,
			activeRemixConversation?.id,
			isRemixCancelled,
			setForceSkipAutoConversationSelection,
			chatMessages,
			stagingAreaTargetMode,
			onPreviewOpened,
			onSubmitted,
			onPreviewDismissed,
		],
	);

	const sendAIFCPrompt = useCallback(
		async ({
			userPrompt,
			promptText: promptTextOverride,
			dynamicUiSubtype,
			dynamicUiType,
			dynamicUiSource,
			interactionSource,
			forcedContentType,
			additionalCreationContextParams,
			conversationId: reuseConversationId,
			remixCancellationSessionId,
		}: {
			userPrompt?: string;
			/** When provided, overrides the auto-generated prompt text sent to Rovo. */
			promptText?: string;
			dynamicUiType: RemixOptionSelectionParameters['type'];
			dynamicUiSubtype?: string;
			dynamicUiSource: DynamicUiSource | undefined;
			interactionSource?: InteractionSource;
			forcedContentType?: string;
			additionalCreationContextParams?: RemixAdditionalCreationContextParams;
			/** Caller-minted conversation. Variant 2 also selects it in Rovo. */
			conversationId?: string;
			/** Exact renderer shadow generation to activate after conversation reset. */
			remixCancellationSessionId?: string;
		}): Promise<string> => {
			const defaultPromptText = 'Remix the selected content';
			const selectedDisplayName = dynamicUiSubtype
				? getSubTypeDisplayName(dynamicUiSubtype)
				: getTypeDisplayName(dynamicUiType);
			const autoText = `Remix the selected content as a ${selectedDisplayName}`;
			let promptText: string;
			if (promptTextOverride) {
				promptText = promptTextOverride;
			} else if (dynamicUiType === 'custom') {
				promptText = userPrompt ?? defaultPromptText;
			} else {
				promptText = userPrompt ? `${autoText}. ${userPrompt}` : autoText;
			}

			// Build the ADF DocNode prompt.
			const prompt: DocNode = {
				type: 'doc',
				version: 1,
				content: [
					{
						type: 'paragraph',
						content: [
							{
								type: 'text',
								text: promptText,
							},
						],
					},
				],
			};

			const newConversationId = reuseConversationId ?? uuid();
			outerCancellationIntentRef.current = remixCancellationSessionId
				? {
						sessionId: remixCancellationSessionId,
						conversationId: newConversationId,
						scope: 'outer-lifecycle',
					}
				: undefined;
			// Store the ID so we can identify this conversation as the active remix.
			setRemixConversationId(newConversationId);
			actionReadyFired.current = false;

			if (
				reuseConversationId &&
				UNSAFE_expValEqualsNoExposure('cc-ce-maui-remix-menu-multivariant', 'cohort', 'variant2')
			) {
				setSelectedConversation({ conversationId: reuseConversationId });
			}

			await createConversationOptimistically({
				id: newConversationId,
				...(shouldTrackRemixConversationById ? { isViewModeRemix: true } : {}),
			});

			if (remixCancellationSessionId) {
				// A reused Rovo conversation can still expose its previous generation's terminal
				// state. Activate only this admitted session after optimistic reset completes.
				activateRemixCancellationObservation({
					conversationId: newConversationId,
					sessionId: remixCancellationSessionId,
				});
			}

			let creationContextExperienceParams = {};
			if (!additionalCreationContextParams) {
				if (dynamicUiType === 'custom') {
					creationContextExperienceParams = { experience: 'remix_custom' as const };
				} else {
					creationContextExperienceParams = {
						experience: 'remix' as const,
						blocks: [dynamicUiType],
					};
				}
			}

			// Send the message to Rovo
			sendMessage({
				prompt,
				storeUserMessages: preferences.storeUserMessages,
				conversationId: newConversationId,
				messageSources: {
					interactionSource,
					// Use the same source for follow-ups: Remix doesn't have a separate follow-up entry point, but the "viewed" AIMAU analytics event uses `followUpSource`.
					followUpSource: interactionSource,
				},
				productKey: 'confluence',
				contentContext: 'staging-area',
				creationContextParams: {
					dynamicUiType,
					dynamicUiSubtype,
					dynamicUiSource,
					source: 'remix',
					...creationContextExperienceParams,
					...(forcedContentType ? { forcedContentType } : {}),
					...additionalCreationContextParams,
					...(dynamicUiType !== 'custom' && userPrompt ? { hasUserInstruction: true } : {}),
				},
			});

			// Store the prompt text for display in the staging area header.
			// Always show the brand keyword regardless of the user's custom prompt.
			// cc_maui_create_keyword: swap "Remix" -> "Create". Use the no-exposure variant since the
			// create-keyword exposure has already fired upstream when the remix flow was triggered.
			setPrompt(
				UNSAFE_expValEqualsNoExposure('cc_maui_create_keyword', 'isEnabled', true)
					? 'Create'
					: 'Remix',
			);

			// Persist the dynamic UI context so the update flow can use
			// the original type/source when sending remix adjustment messages
			setDynamicUiContext(dynamicUiType, dynamicUiSubtype, dynamicUiSource);

			// Keep the user's pick; setDynamicUiContext above gets overwritten with the
			// backend subtype on response, and EditWithRovoBar needs both to flag a mismatch.
			setUserSelectedDynamicUiSubtype(dynamicUiSubtype);

			setInteractionSource(interactionSource);

			return newConversationId;
		},
		[
			createConversationOptimistically,
			sendMessage,
			setPrompt,
			setDynamicUiContext,
			setUserSelectedDynamicUiSubtype,
			setInteractionSource,
			setSelectedConversation,
			preferences.storeUserMessages,
			shouldTrackRemixConversationById,
		],
	);

	return {
		sendAIFCPrompt,
		stopRemixing,
		isRemixError,
		isRemixAborted,
		isRemixCancelled,
		remixStoreError,
		openAIFCStagingModal,
		closeAIFCStagingModal: closeModal,
		remixConversationId,
		resetRemixConversationId,
		setSelectedConversation,
	};
};
